/**
 * Authentication API Service
 */

import http, { setToken, removeToken, setRefreshToken, removeRefreshToken, getRefreshToken, clearAllAuth } from '../client';
import { ENDPOINTS, TOKEN_KEY, USER_KEY } from '../config';
import { getTrackedSource } from '../../utils/sourceDetection';

/**
 * Login with phone and OTP (Mongolia)
 * @param {string} phone - Phone number
 * @param {string} otp - OTP code
 * @returns {Promise<Object>}
 */
export const loginWithOtp = async (phone, otp) => {
  try {
    const tracked = getTrackedSource();
    const source = tracked?.source === 'career_page' ? 'CAREER_PAGE' : 'DIIL';
    const response = await http.post(ENDPOINTS.AUTH.PHONE_VERIFY, {
      phone,
      code: otp,  // Backend expects 'code' not 'otp'
      source,
      sourceChannel: tracked?.source || null
    }, { skipAuth: true });

    if (response.success && response.data?.token) {
      setToken(response.data.token);
      // Store refresh token if provided
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      // Backend returns flat user fields, not nested user object
      const user = {
        id: response.data.candidateId,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        phone: response.data.phone,
        profileUrl: response.data.profileUrl,
        isNewUser: response.data.isNewUser
      };
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Redeem the one-click sign-in link an admin texted to a business (/e/:token).
 *
 * Single-use and expiring server-side, so a refusal here is the normal end of a link's
 * life — not an error to retry. The caller falls back to ordinary OTP login.
 *
 * Stores the session exactly as loginWithOtp does; the caller still has to tell
 * TokiContext (setPhoneUser) before the UI treats them as signed in.
 *
 * @param {string} token the UUID from the URL
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>} `data.companyId`
 *   is the company to land on.
 */
export const loginWithEmployerLink = async (token) => {
  try {
    const response = await http.post(
      ENDPOINTS.AUTH.EMPLOYER_LINK(token), {}, { skipAuth: true }
    );

    if (response.success && response.data?.token) {
      setToken(response.data.token);
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      const user = {
        id: response.data.candidateId,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        phone: response.data.phone,
        profileUrl: response.data.profileUrl,
        isNewUser: response.data.isNewUser
      };
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Direct phone login without OTP (UZ/KZ regions)
 * @param {string} phone - Phone number
 * @param {string} country - Country code (UZ, KZ)
 * @returns {Promise<Object>}
 */
export const loginWithPhone = async (phone, country) => {
  try {
    const tracked = getTrackedSource();
    const source = tracked?.source === 'career_page' ? 'CAREER_PAGE' : 'DIIL';
    const response = await http.post(ENDPOINTS.AUTH.PHONE_LOGIN, {
      phone,
      country,
      source,
      sourceChannel: tracked?.source || null
    }, { skipAuth: true });

    if (response.success && response.data?.token) {
      setToken(response.data.token);
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      const user = {
        id: response.data.candidateId,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        phone: response.data.phone,
        profileUrl: response.data.profileUrl,
        isNewUser: response.data.isNewUser
      };
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Send OTP to phone (Mongolia)
 * @param {string} phone - Phone number
 * @returns {Promise<Object>}
 */
export const sendOtp = async (phone) => {
  try {
    const response = await http.post(ENDPOINTS.AUTH.PHONE_SEND_OTP, {
      phone
    }, { skipAuth: true });

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Send E-Mongolia request (OTP/RegNum login)
 * @param {string} regnum - Register number
 * @param {Array<string>} requestedServices - List of services to request (e.g. ['ID_CARD', 'ADDRESS'])
 * @returns {Promise<Object>}
 */
export const sendEmongoliaOtp = async (regnum, requestedServices = []) => {
  try {
    const payload = { regnum };
    if (requestedServices.length > 0) {
      payload.requestedServices = requestedServices;
    }
    const response = await http.post(ENDPOINTS.AUTH.EMONGOLIA_SEND_OTP, payload, { skipAuth: false });

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Verify E-Mongolia OTP
 * @param {string} regnum - Register number
 * @param {string} otp - OTP Code
 * @param {Array<string>} requestedServices - List of services to request (e.g. ['ID_CARD', 'ADDRESS'])
 * @returns {Promise<Object>}
 */
export const loginWithEmongoliaOtp = async (regnum, otp, requestedServices = []) => {
  try {
    const tracked = getTrackedSource();
    const source = tracked?.source === 'career_page' ? 'CAREER_PAGE' : 'DIIL';
    const payload = { regnum, otp, source, sourceChannel: tracked?.source || null };
    if (requestedServices.length > 0) {
      payload.requestedServices = requestedServices;
    }
    const response = await http.post(ENDPOINTS.AUTH.EMONGOLIA_VERIFY, payload, { skipAuth: false });

    if (response.success && response.data?.token) {
      setToken(response.data.token);
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      const user = {
        id: response.data.candidateId,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        phone: response.data.phone,
        profileUrl: response.data.profileUrl,
        isNewUser: response.data.isNewUser
      };
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Sync E-Mongolia profile for current authenticated user
 * @param {string} regnum - Register number
 * @param {string} otp - OTP Code
 * @param {Array<string>} requestedServices - List of services 
 * @returns {Promise<Object>}
 */
export const syncEmongoliaProfile = async (regnum, otp, requestedServices = []) => {
  try {
    const payload = { regnum, otp };
    if (requestedServices.length > 0) {
      payload.requestedServices = requestedServices;
    }
    const response = await http.post(ENDPOINTS.CANDIDATES.EMONGOLIA_COMPLETE, payload);

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Complete manual profile using the /manual-complete endpoint
 * @param {Object} data - Profile data containing lastName, firstName, regNum, address, lastJob, salary, duration
 * @returns {Promise<Object>}
 */
export const completeManualProfile = async (data) => {
  try {
    const response = await http.post(ENDPOINTS.CANDIDATES.MANUAL_COMPLETE, data);
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Login with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>}
 */
export const login = async (email, password) => {
  try {
    const response = await http.post(ENDPOINTS.AUTH.LOGIN, {
      email,
      password
    }, { skipAuth: true });

    if (response.success && response.data?.token) {
      setToken(response.data.token);
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      if (response.data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
      }
    }

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Register new user
 * @param {Object} userData
 * @returns {Promise<Object>}
 */
export const register = async (userData) => {
  try {
    const response = await http.post(ENDPOINTS.AUTH.REGISTER, userData, { skipAuth: true });

    if (response.success && response.data?.token) {
      setToken(response.data.token);
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      if (response.data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
      }
    }

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Logout user - blacklists refresh token on backend and clears all local auth
 * @returns {Promise<Object>}
 */
export const logout = async () => {
  try {
    const currentRefreshToken = getRefreshToken();
    await http.post(ENDPOINTS.AUTH.LOGOUT, {
      refreshToken: currentRefreshToken || null
    });
  } catch {
    // Ignore logout API errors - local cleanup still happens
  } finally {
    clearAllAuth();
    // Notify all listeners (contexts, App.js) to clear React state and caches
    window.dispatchEvent(new CustomEvent('auth:sessionExpired'));
  }

  return { success: true };
};

/**
 * Refresh auth token using stored refresh token
 * @returns {Promise<Object>}
 */
export const refreshToken = async () => {
  try {
    const currentRefreshToken = getRefreshToken();
    if (!currentRefreshToken) {
      return { success: false, error: 'No refresh token available' };
    }

    const response = await http.post(ENDPOINTS.AUTH.REFRESH, {
      refreshToken: currentRefreshToken
    }, { skipAuth: true });

    if (response.success && response.data?.token) {
      setToken(response.data.token);
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
    }

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get stored user
 * @returns {Object|null}
 */
export const getStoredUser = () => {
  try {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  try {
    return !!localStorage.getItem(TOKEN_KEY);
  } catch {
    return false;
  }
};

export default {
  loginWithOtp,
  loginWithPhone,
  sendOtp,
  sendEmongoliaOtp,
  loginWithEmongoliaOtp,
  syncEmongoliaProfile,
  completeManualProfile,
  login,
  register,
  logout,
  refreshToken,
  getStoredUser,
  isAuthenticated
};
